// src/components/crm/CustomerCombobox.js
//
// custpick-8e5a2c71 — a type-to-filter customer picker.
//
// WHY THIS EXISTS. Job and quote creation each used a native <select> over the
// whole customer list. A native select only does browser type-ahead, which JUMPS
// to a match instead of narrowing the list — so on an org with 3,274 customers
// (Alberni) picking a customer meant scrolling, and typing made the list leap
// around. This narrows as you type, the way the customers list and the mobile
// job-builder picker already do.
//
// THE ONE RULE THAT KEEPS THIS SAFE. Selecting a customer has a long tail of side
// effects in both forms — address autofill, service-location arming, bill-to
// reset, geocode inheritance, the sticky note appended to Description, tag chips,
// the sales-rep credit line. None of it is reimplemented here and none of those
// handlers were edited. This calls:
//
//     onChange({ target: { name, value: customerId } })
//
// which is exactly the shape a native <select> hands its onChange, so each form's
// existing handleCustomerChange / handleBillToThirdPartyChange runs verbatim.
// Clearing fires the same thing with '' — the identical path as picking the blank
// option, which is what those handlers already treat as "no customer".
//
// MATCHING is customerMatchesQuery from utils/customerDisplay — the shared helper,
// the same one the customers list uses and a byte-for-byte match for the mobile
// picker's behaviour. Deliberately not reimplemented here: a picker with its own
// private idea of what "matches" means is how three different search behaviours
// got into one app in the first place.

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';
import { buildCustomerDisplayName, customerMatchesQuery } from '../../utils/customerDisplay';

// Rendering thousands of rows is the thing this component exists to avoid, so the
// list is capped and the cap is DISCLOSED — a silent cap would quietly hide the
// customer someone is hunting for and look like a missing record.
const MAX_ROWS = 50;

// What a row shows. `name` first so labels are byte-identical to the <select>
// options these replace; the builder is the fallback that makes mobile-created
// records (no top-level `name`) legible instead of blank. Both are searchable —
// see the custpick-8e5a2c71 note in customerDisplay.js.
const rowLabel = (c) => c?.name || buildCustomerDisplayName(c) || 'Unnamed customer';

const CustomerCombobox = ({
    customers = [],
    value = '',
    onChange,
    name,
    disabled = false,
    placeholder = 'Search customers by name, address, phone…',
    emptyLabel = '-- Select a Customer --',
    className = '',
    invalid = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [highlight, setHighlight] = useState(0);
    const wrapRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    // Stable id so the input's aria-controls can point at the list. useRef, not
    // useState, because it must not change across renders.
    const listboxId = useRef(`customer-combobox-${Math.random().toString(36).slice(2, 9)}`).current;

    const selected = useMemo(
        () => customers.find(c => c.id === value) || null,
        [customers, value]
    );

    // The full match set, and the capped slice actually rendered. `total` drives
    // the footer, so the count reported is of real matches, not of rows drawn.
    const { rows, total } = useMemo(() => {
        const matches = query.trim()
            ? customers.filter(c => customerMatchesQuery(c, query))
            : customers;
        return { rows: matches.slice(0, MAX_ROWS), total: matches.length };
    }, [customers, query]);

    // Close on outside mousedown. mousedown (not click) so a row's own click still
    // lands before the list unmounts.
    useEffect(() => {
        if (!isOpen) return;
        const onDocMouseDown = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Keep the highlighted row in view while arrowing through a long list.
    useEffect(() => {
        if (!isOpen || !listRef.current) return;
        const el = listRef.current.querySelector(`[data-idx="${highlight}"]`);
        if (el) el.scrollIntoView({ block: 'nearest' });
    }, [highlight, isOpen]);

    // Closing always discards the typed query and restores the display to the
    // current selection — Escape and click-away leave the field exactly as found.
    const close = () => {
        setIsOpen(false);
        setQuery('');
        setHighlight(0);
    };

    const open = () => {
        if (disabled) return;
        setIsOpen(true);
        setHighlight(0);
    };

    const emit = (customerId) => {
        // The native-<select> event shape. See the header note.
        if (onChange) onChange({ target: { name, value: customerId } });
    };

    const pick = (customer) => {
        emit(customer.id);
        close();
    };

    const clear = (e) => {
        e.stopPropagation();
        if (disabled) return;
        emit('');
        close();
        inputRef.current?.focus();
    };

    const onKeyDown = (e) => {
        if (disabled) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!isOpen) { open(); return; }
            setHighlight(h => Math.min(h + 1, rows.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (!isOpen) return;
            setHighlight(h => Math.max(h - 1, 0));
        } else if (e.key === 'Enter') {
            // Only swallow Enter when it is actually selecting something —
            // otherwise it must stay available to the surrounding form.
            if (isOpen && rows[highlight]) {
                e.preventDefault();
                pick(rows[highlight]);
            }
        } else if (e.key === 'Escape') {
            if (isOpen) {
                e.preventDefault();
                e.stopPropagation();  // don't also close the modal behind this
                close();
            }
        } else if (e.key === 'Tab') {
            close();
        }
    };

    // Closed: show the selection (or the placeholder label). Open: show what is
    // being typed, with the selection as placeholder so context is never lost.
    const inputValue = isOpen ? query : (selected ? rowLabel(selected) : '');

    return (
        <div ref={wrapRef} className={`relative ${className}`}>
            <div className="relative">
                <Search
                    size={14}
                    className={`absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none ${disabled ? 'text-gray-300' : 'text-gray-400'}`}
                />
                <input
                    ref={inputRef}
                    type="text"
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-controls={listboxId}
                    aria-autocomplete="list"
                    autoComplete="off"
                    disabled={disabled}
                    value={inputValue}
                    placeholder={selected && !isOpen ? '' : (isOpen ? placeholder : emptyLabel)}
                    onFocus={open}
                    onMouseDown={() => { if (!isOpen) open(); }}
                    onChange={(e) => { if (!isOpen) setIsOpen(true); setQuery(e.target.value); setHighlight(0); }}
                    onKeyDown={onKeyDown}
                    className={`mt-1 w-full pl-8 pr-14 p-2 border rounded-md bg-white disabled:bg-gray-100 disabled:text-gray-500 ${invalid ? 'border-red-400' : ''}`}
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                    {selected && !disabled && (
                        <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={clear}
                            title="Clear selection"
                            className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                        >
                            <X size={14} />
                        </button>
                    )}
                    <ChevronDown size={14} className={disabled ? 'text-gray-300' : 'text-gray-400'} />
                </div>
            </div>

            {isOpen && !disabled && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
                    <div ref={listRef} id={listboxId} role="listbox" className="max-h-60 overflow-y-auto">
                        {rows.length === 0 ? (
                            <p className="px-3 py-3 text-sm text-gray-500">No customers match “{query.trim()}”.</p>
                        ) : (
                            rows.map((c, idx) => {
                                const isHighlighted = idx === highlight;
                                const isSelected = c.id === value;
                                return (
                                    <button
                                        key={c.id}
                                        type="button"
                                        role="option"
                                        aria-selected={isSelected}
                                        data-idx={idx}
                                        onMouseDown={(e) => e.preventDefault()}  // keep focus; let onClick run
                                        onMouseEnter={() => setHighlight(idx)}
                                        onClick={() => pick(c)}
                                        className={`w-full text-left px-3 py-2 text-sm border-b border-gray-50 last:border-b-0 ${
                                            isHighlighted ? 'bg-indigo-50' : 'bg-white'
                                        } ${isSelected ? 'font-semibold text-indigo-700' : 'text-gray-700'}`}
                                    >
                                        <span className="block truncate">{rowLabel(c)}</span>
                                        {(c.address1 || c.city || c.phone) && (
                                            <span className="block truncate text-xs text-gray-400">
                                                {[c.address1, c.city, c.phone].filter(Boolean).join(' · ')}
                                            </span>
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>
                    {total > MAX_ROWS && (
                        <p className="px-3 py-2 text-xs text-gray-500 border-t border-gray-100 bg-gray-50">
                            Showing first {MAX_ROWS} of {total} matches — keep typing to narrow.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default CustomerCombobox;
